/**
 * Create-pack form for S4 (WF-F2). Produces a draft Industry Pack shell; thick
 * authoring continues on the sectioned pack editor.
 */

import React, { useState } from 'react';
import { useLanguage } from '@/state/language/LanguageProvider';
import type { CreatePackRequest } from '@/types/aiStudioAgent';

interface CreatePackFormProps {
  submitting: boolean;
  onSubmit: (request: CreatePackRequest) => void;
  onCancel: () => void;
}

const FIELD_CLASS =
  'w-full rounded border border-[var(--color-border)] bg-[var(--color-surface-bg)] px-3 py-2 text-base text-[var(--color-surface-text)] focus:outline-none focus:ring-2 focus:ring-[var(--color-info)]';
const LABEL_CLASS = 'mb-1 block text-base font-medium text-[var(--color-surface-text)]';
const HINT_CLASS = 'mt-1 text-base text-[var(--color-surface-text-secondary)]';

export const CreatePackForm: React.FC<CreatePackFormProps> = ({
  submitting,
  onSubmit,
  onCancel,
}) => {
  const { t } = useLanguage();
  const [name, setName] = useState('');
  const [domain, setDomain] = useState('');
  const [isPlatform, setIsPlatform] = useState(true);
  const [validationError, setValidationError] = useState<string | null>(null);

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!name.trim()) {
      setValidationError(t('aiStudio.packs.form.requiredFields'));
      return;
    }
    setValidationError(null);
    onSubmit({
      name: name.trim(),
      domain: domain.trim() || null,
      isPlatform,
    });
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="rounded border border-[var(--color-border)] bg-[var(--color-secondary-bg)] p-4"
    >
      <h2 className="mb-3 text-base font-semibold text-[var(--color-surface-text)]">
        {t('aiStudio.packs.form.title')}
      </h2>

      <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-4">
        <div>
          <label className={LABEL_CLASS} htmlFor="pack-name">
            {t('aiStudio.packs.form.name')}
          </label>
          <input
            id="pack-name"
            className={FIELD_CLASS}
            value={name}
            onChange={(event) => setName(event.target.value)}
            autoComplete="off"
          />
        </div>

        <div>
          <label className={LABEL_CLASS} htmlFor="pack-domain">
            {t('aiStudio.packs.form.domain')}
          </label>
          <input
            id="pack-domain"
            className={FIELD_CLASS}
            value={domain}
            onChange={(event) => setDomain(event.target.value)}
            autoComplete="off"
          />
        </div>

        <div className="flex flex-col justify-end">
          <label className="flex items-start gap-2 text-base text-[var(--color-surface-text)]">
            <input
              type="checkbox"
              checked={isPlatform}
              onChange={(event) => setIsPlatform(event.target.checked)}
              className="mt-1"
            />
            <span>
              {t('aiStudio.packs.form.isPlatform')}
              <span className={`block ${HINT_CLASS}`}>{t('aiStudio.packs.form.isPlatformHint')}</span>
            </span>
          </label>
        </div>
      </div>

      {validationError && (
        <p role="alert" className="mt-3 text-base text-[var(--color-error)]">
          {validationError}
        </p>
      )}

      <div className="mt-4 flex gap-2">
        <button
          type="submit"
          disabled={submitting}
          className="rounded-md bg-[var(--color-accent-bg)] px-4 py-2 text-base text-[var(--color-accent-text)] hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-[var(--color-info)] disabled:opacity-50"
        >
          {submitting ? t('aiStudio.common.saving') : t('aiStudio.common.create')}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={submitting}
          className="rounded-md border border-[var(--color-border)] px-4 py-2 text-base text-[var(--color-surface-text)] hover:bg-[var(--color-surface-hover)] disabled:opacity-50"
        >
          {t('aiStudio.common.cancel')}
        </button>
      </div>
    </form>
  );
};
